import { VStack } from '@chakra-ui/react';
import { Typography } from 'Tokens';
import {
  ActionFieldsFragment_,
  ActionQuestionFieldsFragment_,
  ReportActionQuestionsQuery_,
  ReportTargetQuestionsQuery_,
  TargetFieldsFragment_,
  TargetQuestionFieldsFragment_,
} from 'models';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DREnums, FrameworkRefs } from 'containers/Esrs';
import { getDisclosureRequirementLink } from 'utils/links';
import {
  ClimateTargetQuestionRefs,
  CompletionStatus,
  NoAnswerBox,
  QuestionStatusBar,
  adjustMDRReportData,
  getReportMDRA,
} from '../ReportUtils';
import { EsrsReportStandardSwitch, ReportMDR } from '../Report.types';
import { mapUnitToCompanyCurrency, percentageToNumber } from 'containers/Esrs/utils';
import { Table } from 'Molecules';
import { CellContext } from '@tanstack/react-table';
import { sortBy } from 'lodash';
import { useCurrentCompany } from 'utils/hooks';

const getTargetLink = (target: TargetFieldsFragment_, framework: string) => {
  return `${getDisclosureRequirementLink({
    companyId: target.materialStandard.esrsAssessment.company.id,
    assessmentId: target.materialStandard.esrsAssessment.id,
    standardRef: target.materialStandard.standardRef,
    disclosureRequirement: {
      reference: target.disclosureRequirementRef ?? '',
      type: DREnums.target,
    },
    framework,
  })}/target-form/${target.id}/edit`;
};

const getActionLink = (action: ActionFieldsFragment_, framework: string) => {
  return `${getDisclosureRequirementLink({
    companyId: action.materialStandard.esrsAssessment.company.id,
    assessmentId: action.materialStandard.esrsAssessment.id,
    standardRef: action.materialStandard.standard.reference,
    disclosureRequirement: {
      reference: action.disclosureRequirementRef ?? '',
      type: DREnums.target,
    },
    framework,
  })}/target-form/${action.id}/edit`;
};

export const TargetSummaryTable = ({ targets }: { targets: TargetFieldsFragment_[] }) => {
  const nbMilestoneYears = useMemo(
    () => Math.max(...targets.map((t) => t.milestones?.length ?? 0)),
    [targets]
  );
  const milestoneMap = useMemo(
    () => Array.from({ length: nbMilestoneYears }, (_, i) => i),
    [nbMilestoneYears]
  );
  const company = useCurrentCompany();
  const companyCurrency = useMemo(() => company.company?.currency, [company]);
  return (
    <Table<TargetFieldsFragment_>
      withBorder={true}
      columns={[
        {
          header: 'Target name',
          accessorKey: 'name',
          cell: ({ row }) => <Typography variant="body">{row.original.name}</Typography>,
        },
        {
          header: 'Related metric',
          accessorKey: 'title',
          cell: ({ row }) => {
            const unit = mapUnitToCompanyCurrency(
              row.original.metric?.unitOfMeasurement ?? 't',
              companyCurrency ?? ''
            );
            return (
              <Typography variant="body">
                {row.original.metric?.title} ({unit === 't' ? 'tonnes' : unit})
              </Typography>
            );
          },
        },
        {
          header: 'Baseline value',
          accessorKey: 'baseline',
          cell: ({ row }) => {
            const baseline = row.original.keyResults.find((kr) => kr.reportingUnitId === null);
            return <Typography variant="body">{baseline?.baseline ?? 'N/A'}</Typography>;
          },
        },
        {
          header: 'Baseline year',
          accessorKey: 'year',
          cell: ({ row }) => {
            const baseline = row.original.keyResults.find((kr) => kr.reportingUnitId === null);
            return (
              <Typography variant="body">
                {baseline?.baseYear ? new Date(baseline?.baseYear).getUTCFullYear() : 'N/A'}
              </Typography>
            );
          },
        },
        ...milestoneMap.map((index) => ({
          header: index === nbMilestoneYears - 1 ? 'End target' : `Milestone ${index + 1}`,
          accessorKey: 'milestone',
          cell: (row: CellContext<TargetFieldsFragment_, unknown>) => {
            const milestones = sortBy(row.row.original.milestones, 'year');
            const baseline = row.row.original.keyResults.find(
              (kr) => kr.reportingUnitId === null
            )?.baseline;
            const milestone = milestones?.[index];
            const milestoneResult = milestone?.milestoneResults?.find(
              (mr) => mr.reportingUnitId === null
            )?.value;
            const targetValue = percentageToNumber(milestoneResult, Number(baseline));

            if (!milestone) return <></>;

            if (isNaN(milestoneResult) || isNaN(targetValue))
              return <Typography variant="body">N/A</Typography>;

            return (
              <Typography variant="body">
                {targetValue} ({milestone.year})
              </Typography>
            );
          },
        })),
      ]}
      allowSorting={false}
      bottomRowBorder={false}
      borderRadius="0px"
      rowHeight="36px"
      headerHeight="36PX"
      cellPadding="8px"
      headerPadding="8px"
      data={targets}
    />
  );
};

const MDRCard = ({
  target,
  action,
  mdr,
  isPreview,
  standard,
  link,
}: {
  target?: TargetFieldsFragment_;
  action?: ActionFieldsFragment_;
  mdr: ReportMDR;
  isPreview: boolean;
  standard: EsrsReportStandardSwitch;
  link: string;
}) => {
  const navigate = useNavigate();
  const mdrAnswer = useMemo(() => {
    const field = mdr?.secondField ?? mdr.field;
    const answer = !!target
      ? target?.[field as keyof TargetFieldsFragment_]
      : action?.[field as keyof ActionFieldsFragment_];
    return mdr?.value ?? answer;
  }, [target, action, mdr]);

  const provideAnswer = () => navigate(link);

  const status = useMemo(() => {
    if (mdrAnswer) {
      return CompletionStatus.complete;
    }
    return CompletionStatus.incomplete;
  }, [mdrAnswer]);

  if (mdr.field === 'none') {
    return (
      <VStack
        alignItems="start"
        borderTop={isPreview ? '' : '1px solid'}
        width="100%"
        borderColor="border.decorative"
        pt="6px"
      >
        <Typography variant="bodyStrong">{mdr.title}</Typography>
      </VStack>
    );
  }

  return (
    <VStack
      alignItems="start"
      borderTop={isPreview ? '' : '1px solid'}
      width={mdr.isChild ? `calc(100% - 28px)` : '100%'}
      borderColor="border.decorative"
      pt="6px"
      spacing="6px"
      pb="16px"
      id={`${mdr.field}`}
      ml={mdr.isChild && !isPreview ? '28px' : ''}
    >
      {!isPreview && (
        <QuestionStatusBar
          status={status}
          standardName={standard.title}
          standardRef={standard.reference ?? ''}
          completed={!!mdrAnswer}
          link={link}
        />
      )}
      <VStack alignItems="start" w="100%" spacing="4px">
        <Typography variant="bodyStrong">{mdr.title}</Typography>
        {!(mdr.hideAnswer && !!mdrAnswer) &&
          (mdrAnswer ? (
            <Typography variant="body" whiteSpace="pre-wrap">
              {mdrAnswer}
            </Typography>
          ) : (
            <NoAnswerBox isPreview={isPreview} provideAnswer={provideAnswer} />
          ))}
      </VStack>
    </VStack>
  );
};

const TargetQuestionCard = ({
  answers,
  question,
  isPreview,
  standard,
  isChild,
  link,
}: {
  answers: any;
  question: ActionQuestionFieldsFragment_ | TargetQuestionFieldsFragment_;
  isPreview: boolean;
  standard: EsrsReportStandardSwitch;
  isChild?: boolean;
  link: string;
}) => {
  const navigate = useNavigate();
  const answer = useMemo(() => answers?.[question.reference], [answers]);

  const provideAnswer = () => navigate(link);

  const status = useMemo(() => {
    if (answer?.value) {
      return CompletionStatus.complete;
    }
    return CompletionStatus.incomplete;
  }, [answer]);

  return (
    <VStack
      alignItems="start"
      borderTop={isPreview ? '0px' : '1px solid'}
      width={isChild ? `calc(100% - 28px)` : '100%'}
      borderColor="border.decorative"
      pt="6px"
      spacing="6px"
      pb="16px"
      id={`${question.reference}`}
      ml={isChild && !isPreview ? '28px' : ''}
    >
      {!isPreview && (
        <QuestionStatusBar
          status={status}
          standardName={standard.title}
          standardRef={standard.reference ?? ''}
          completed={!!answer?.value}
          link={link}
        />
      )}
      <VStack alignItems="start" w="100%" spacing="4px">
        <Typography variant="bodyStrong">{question.title}</Typography>
        {answer?.value ? (
          <Typography variant="body" whiteSpace="pre-wrap">
            {answer?.value}
          </Typography>
        ) : (
          <NoAnswerBox isPreview={isPreview} provideAnswer={provideAnswer} />
        )}
      </VStack>
    </VStack>
  );
};

export const ReportTargetDR = ({
  target,
  isPreview,
  standard,
  targetQuestions,
  framework,
}: {
  target: TargetFieldsFragment_;
  isPreview: boolean;
  standard: EsrsReportStandardSwitch;
  targetQuestions: ReportTargetQuestionsQuery_ | undefined;
  framework: string;
}) => {
  const isClimateChange = useMemo(() => standard.reference === 'ESRS-E1', [standard]);

  const reportTargetsMDR = useMemo(
    () =>
      adjustMDRReportData({ target, isDocx: false, isPreview, isClimateChange, targetQuestions }),
    [target, isPreview, isClimateChange, targetQuestions]
  );

  const targetQuestionsData = useMemo(() => {
    if (!isClimateChange) return targetQuestions;
    const filteredSingle =
      targetQuestions?.singleQuestions.filter(
        (question) => !ClimateTargetQuestionRefs.includes(question.reference)
      ) ?? [];

    const filterGroups = targetQuestions?.groups.map((group) => ({
      ...group,
      targetQuestions:
        group.targetQuestions.filter(
          (question) => !ClimateTargetQuestionRefs.includes(question.reference)
        ) ?? [],
    }));
    return { singleQuestions: filteredSingle, groups: filterGroups };
  }, [isClimateChange, targetQuestions]);

  const link = useMemo(() => getTargetLink(target, framework), [target, framework]);

  return (
    <VStack alignItems="start" width="100%" spacing="16px">
      <Typography variant="h3">{target.name}</Typography>
      {framework === FrameworkRefs.esrs && (
        <>
          <Typography variant="h4">MDR-T</Typography>
          {reportTargetsMDR.map((mdr) => (
            <MDRCard
              target={target}
              mdr={mdr as ReportMDR}
              isPreview={isPreview}
              standard={standard}
              link={link}
            />
          ))}
        </>
      )}
      <Typography variant="h4">Other disclosures</Typography>
      {targetQuestionsData?.groups?.map((group) => (
        <VStack alignItems="start" width="100%">
          <Typography variant="bodyStrong">{group.title}</Typography>
          {group.targetQuestions.map((question) => (
            <TargetQuestionCard
              answers={target.answers}
              question={question}
              isPreview={isPreview}
              standard={standard}
              isChild={true}
              link={link}
            />
          ))}
        </VStack>
      ))}
      {targetQuestionsData?.singleQuestions?.map((question) => (
        <TargetQuestionCard
          answers={target.answers}
          question={question}
          isPreview={isPreview}
          standard={standard}
          link={link}
        />
      ))}
    </VStack>
  );
};

export const ReportActionDR = ({
  action,
  actionQuestions,
  isPreview,
  standard,
  framework,
}: {
  action: ActionFieldsFragment_;
  actionQuestions: ReportActionQuestionsQuery_ | undefined;
  isPreview: boolean;
  standard: EsrsReportStandardSwitch;
  framework: string;
}) => {
  const actionsMDR = getReportMDRA();

  const link = useMemo(() => getActionLink(action, framework), [action, framework]);

  return (
    <VStack alignItems="start" width="100%" spacing="16px">
      <Typography variant="h3">{action.title}</Typography>
      {framework === FrameworkRefs.esrs && (
        <>
          <Typography variant="h4">MDR-A</Typography>
          {actionsMDR.map((mdr) => (
            <MDRCard
              action={action}
              mdr={mdr}
              isPreview={isPreview}
              standard={standard}
              link={link}
            />
          ))}
        </>
      )}
      <Typography variant="h4">Other disclosures</Typography>
      {actionQuestions?.groups?.map((group) => (
        <VStack alignItems="start" width="100%">
          <Typography variant="bodyStrong">{group.title}</Typography>
          {group.actionQuestions.map((question) => (
            <TargetQuestionCard
              answers={action.answers}
              question={question}
              isPreview={isPreview}
              standard={standard}
              isChild={true}
              link={link}
            />
          ))}
        </VStack>
      ))}
      {actionQuestions?.singleQuestions?.map((question) => (
        <TargetQuestionCard
          answers={action.answers}
          question={question}
          isPreview={isPreview}
          standard={standard}
          link={link}
        />
      ))}
    </VStack>
  );
};
